package fr.arcplex.sooner.webui

import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.searchInputItem

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusDirection
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.OffsetMapping
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.TransformedText
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import fr.arcplex.sooner.coherence.CoherenceThemeState
import fr.arcplex.sooner.ui.theme.TVBackground
import fr.arcplex.sooner.ui.theme.TVMuted
import fr.arcplex.sooner.ui.theme.TVPlay
import fr.arcplex.sooner.ui.theme.TVTxt

/**
 * Skin of the search input. The legacy view uses the defaults below, the
 * Coherence view derives the same fields from CoherenceThemeState.
 */
data class SearchStyle(
    val background: Color = TVBackground,
    val textColor: Color = TVTxt,
    val placeholderColor: Color = TVMuted,
    val accent: Color = TVPlay,
    val labelColor: Color = TVMuted,
    val cornerRadius: Dp = 0.dp,
    val paddingX: Dp = 12.dp,
    val paddingY: Dp = 10.dp,
    val textSize: TextUnit = 18.sp,
    val labelSize: TextUnit = 13.sp,
    val placeholder: String = "Recherche"
)

/**
 * Search input of the legacy server-driven UI: a standard Compose text field
 * backed by the platform IME (no on-screen key grid is ported).
 */
@Composable
fun SearchView(
    rail: Rail,
    query: String,
    onQueryChange: (String) -> Unit,
    onValidate: () -> Unit,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    SearchPanel(
        rail = rail,
        query = query,
        onQueryChange = onQueryChange,
        onValidate = onValidate,
        style = SearchStyle(),
        isTV = isTV,
        modifier = modifier
    )
}

/**
 * Same input with colors, radii, paddings and text sizes coming from the
 * Coherence theme; selected when useCoherence is true.
 */
@Composable
fun CoherenceSearchView(
    rail: Rail,
    query: String,
    onQueryChange: (String) -> Unit,
    onValidate: () -> Unit,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    SearchPanel(
        rail = rail,
        query = query,
        onQueryChange = onQueryChange,
        onValidate = onValidate,
        style = coherenceSearchStyle(),
        isTV = isTV,
        modifier = modifier
    )
}

@Composable
private fun coherenceSearchStyle(): SearchStyle {
    val theme = CoherenceThemeState.theme
    val page = theme.page
    val action = theme.kinds.action
    return SearchStyle(
        background = parseHexColorOrNull(page.surface)
            ?: parseHexColorOrNull(page.background) ?: TVBackground,
        textColor = parseHexColorOrNull(page.text) ?: TVTxt,
        placeholderColor = parseHexColorOrNull(page.muted) ?: TVMuted,
        accent = parseHexColorOrNull(page.accent) ?: TVPlay,
        labelColor = parseHexColorOrNull(page.muted) ?: TVMuted,
        cornerRadius = (action.radius ?: 5).dp,
        paddingX = (action.paddingX ?: 12).dp,
        paddingY = (action.paddingY ?: 8).dp,
        textSize = (theme.kinds.text.size ?: 18).sp
    )
}

@Composable
private fun SearchPanel(
    rail: Rail,
    query: String,
    onQueryChange: (String) -> Unit,
    onValidate: () -> Unit,
    style: SearchStyle,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    val input = rail.searchInputItem()
    val secret = input?.keyboardSecret == true
    val hideData = input?.keyboardHideData == true
    val shape = RoundedCornerShape(style.cornerRadius)
    val focusManager = LocalFocusManager.current
    var focused by remember { mutableStateOf(false) }

    val transformation = when {
        secret -> PasswordVisualTransformation()
        hideData -> HiddenTextTransformation
        else -> VisualTransformation.None
    }

    androidx.compose.foundation.layout.Column(modifier = modifier.fillMaxWidth()) {
        if (!rail.label.isNullOrBlank()) {
            Text(
                text = rail.label.orEmpty(),
                color = style.labelColor,
                fontSize = style.labelSize,
                modifier = Modifier.padding(
                    start = style.paddingX,
                    end = style.paddingX,
                    bottom = style.paddingY
                )
            )
        }

        TextField(
            value = query,
            onValueChange = onQueryChange,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = style.paddingX)
                .then(
                    // The results rail sits above the field: without this the
                    // TextField keeps D-pad Up for cursor movement and the only
                    // way out is Back.
                    if (isTV) Modifier.onPreviewKeyEvent { event ->
                        if (event.type == KeyEventType.KeyDown && event.key == Key.DirectionUp) {
                            focusManager.moveFocus(FocusDirection.Up)
                            true
                        } else {
                            false
                        }
                    } else Modifier
                )
                .onFocusChanged { focused = it.isFocused }
                .then(if (focused && isTV) Modifier.border(2.dp, style.accent, shape) else Modifier),
            singleLine = true,
            shape = shape,
            textStyle = TextStyle(color = style.textColor, fontSize = style.textSize),
            placeholder = {
                if (!hideData) {
                    Text(
                        text = style.placeholder,
                        color = style.placeholderColor,
                        fontSize = style.textSize
                    )
                }
            },
            visualTransformation = transformation,
            keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
            keyboardActions = KeyboardActions(onSearch = { onValidate() }),
            colors = TextFieldDefaults.colors(
                focusedContainerColor = style.background,
                unfocusedContainerColor = style.background,
                focusedTextColor = style.textColor,
                unfocusedTextColor = style.textColor,
                focusedIndicatorColor = Color.Transparent,
                unfocusedIndicatorColor = Color.Transparent,
                cursorColor = style.accent
            )
        )
    }
}

/**
 * `keyboard_hide_datas` keeps the query editable but hides what is typed, the
 * way the original apps hid the search echo.
 */
private val HiddenTextTransformation = VisualTransformation { _ ->
    TransformedText(
        AnnotatedString(""),
        object : OffsetMapping {
            override fun originalToTransformed(offset: Int): Int = 0
            override fun transformedToOriginal(offset: Int): Int = 0
        }
    )
}
